import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["sx", "variant"];
import PropTypes from 'prop-types';
import * as React from 'react';
import MuiIconButton from '@mui/material/IconButton';
import { capitalizeFirstLetter } from '@lumahealthhq/ui-utils';
import { extendSX } from '../helpers';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 *
 * Demos:
 *
 * - [Button](https://ui-packages.isbuildingluma.com/ui-core/react-button/)
 *
 * API:
 *
 * - [IconButton API](https://ui-packages.isbuildingluma.com/ui-core/api/icon-button/)
 * - inherits [MuiIconButton API](https://mui.com/material-ui/api/icon-button/)
 */
const IconButton = /*#__PURE__*/React.forwardRef(function IconButton(_ref, ref) {
  let {
      sx,
      variant = 'default'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return (
    /*#__PURE__*/
    // TODO danielmana: useThemeProps with `LHIconButton` to override variants from theme
    _jsx(MuiIconButton, _extends({
      ref: ref,
      className: `MuiIconButton-variant${capitalizeFirstLetter(variant)}`,
      sx: extendSX(sx, [
      // TODO danielmana: move styles per variant to the theme
      // primary
      variant === 'primary' && (theme => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: `${theme.shadows[1]} !important`,
        '&:hover': {
          backgroundColor: theme.palette.primary.light
        },
        '&:active': {
          backgroundColor: theme.palette.primary.dark
        },
        '&:focus-visible': {
          outline: `4px solid ${theme.palette.tertiary.light}`,
          backgroundColor: theme.palette.primary.light
        },
        '&:disabled': {
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
          opacity: 0.4
        }
      })),
      // secondary
      variant === 'secondary' && (theme => ({
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.black,
        boxShadow: `${theme.shadows[1]} !important`,
        '&:hover': {
          backgroundColor: theme.palette.primary[200]
        },
        '&:active': {
          backgroundColor: theme.palette.primary[300]
        },
        '&:focus-visible': {
          outline: `4px solid ${theme.palette.tertiary.light}`,
          backgroundColor: theme.palette.primary[100]
        },
        '&:disabled': {
          backgroundColor: theme.palette.common.white,
          color: theme.palette.common.black,
          opacity: 0.4
        }
      })),
      // danger
      variant === 'danger' && (theme => ({
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        boxShadow: `${theme.shadows[1]} !important`,
        '&:hover': {
          backgroundColor: theme.palette.error[600]
        },
        '&:active': {
          backgroundColor: theme.palette.error[700]
        },
        '&:focus-visible': {
          outline: `4px solid ${theme.palette.tertiary.light}`,
          backgroundColor: theme.palette.error[600]
        },
        '&:disabled': {
          backgroundColor: theme.palette.error[500],
          color: theme.palette.error.contrastText,
          opacity: 0.4
        }
      })),
      // variant & sizeMedium
      variant !== 'default' && (!rest.size || rest.size === 'medium') && {
        height: 40,
        width: 40,
        '& > svg': {
          fontSize: 18
        }
      },
      // smallBlack
      variant === 'smallBlack' && {
        color: 'common.black',
        '& > svg': {
          fontSize: 16
        }
      }])
    }, rest))
  );
});
process.env.NODE_ENV !== "production" ? IconButton.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit "IconButtonProps.ts" and run "yarn proptypes"
  // ----------------------------------------------------------------------
  /**
   * Defines a string value that labels the current element.
   */
  'aria-label': PropTypes.string.isRequired,
  /**
   * The icon to display.
   */
  children: PropTypes.node,
  /**
   * If `true`, the ripple effect is disabled.
   *
   * ⚠️ Without a ripple there is no styling for :focus-visible by default. Be sure
   * to highlight the element by applying separate styles with the `.Mui-focusVisible` class.
   * @default false
   */
  disableRipple: PropTypes.bool,
  /**
   * The size of the component.
   * `small` is equivalent to the dense button styling.
   * @default 'medium'
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * The variant to use.
   * @default 'default'
   */
  variant: PropTypes.oneOf(['danger', 'default', 'primary', 'secondary', 'smallBlack'])
} : void 0;
export default IconButton;